import * as React from 'react';

import type * as api from '@gitbook/api-client';

import { useUserOrganizationsToJoin } from '../../state';
import { CreateOrganizationScreen } from './CreateOrganizationScreen';
import { OrganizationsListScreen } from './OrganizationsListScreen';

/**
 * Rendered whenever a user is onboarding after sign-up.
 * If they are already members of organizations, we display a list of them.
 * Otherwise we create their first organization and display a screen to choose the name.
 */
export function OnboardingScreen(props: {
    // flow controls
    onNext: ({ organization }: { organization: api.Organization }) => Promise<void>;
}): React.ReactElement {
    const { onNext } = props;

    const organizations = useUserOrganizationsToJoin();

    /**
     * True if the user manually decided to create an org instead of following up with
     * an existing one that they're a member of.
     */
    const [forceCreatingOrg, setForceCreatingOrg] = React.useState(false);
    const [isCreatingOrg, setIsCreatingOrg] = React.useState(false);

    if (organizations.length === 0 || forceCreatingOrg) {
        return (
            <CreateOrganizationScreen
                isClosable={false}
                onClickCancel={
                    forceCreatingOrg && !isCreatingOrg
                        ? () => setForceCreatingOrg(false)
                        : undefined
                }
                onNext={onNext}
                onStartCreating={() => {
                    setIsCreatingOrg(true);
                    setForceCreatingOrg(true);
                }}
            />
        );
    }

    return (
        <OrganizationsListScreen
            organizations={organizations}
            onClickCreateOrg={() => setForceCreatingOrg(true)}
            onNext={onNext}
        />
    );
}
